// ─────────────────────────────────────────────────────────────
// inwardPrint.config.js — Report 9: goods receipt (GRN)
// Shape: statement (landscape) — two sections
//   1. Bill lines   — what to reconcile against the vendor bill
//   2. Rolls received — the physical rolls and their landed cost
// Data: GET /api/tape/inward/:id  (header + items[] + rolls[])
//
// GOODS + GST POSTS TO THE VENDOR; CHARGES DO NOT
// Freight, labour and other charges are paid to third parties on
// receipt, not to the vendor. They load onto the rolls so cost of
// goods reflects what the material cost to get onto the floor.
// The totals box keeps the two apart so the vendor-facing figure
// is never confused with the landed figure.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const inwardPrintConfig = (inw) => {
  const rolls = inw.rolls || [];
  const items = inw.items || [];

  const freight = num(inw.freight_charges);
  const labour = num(inw.labour_charges);
  const other = num(inw.other_charges);
  const charges = freight + labour + other;
  const gst = num(inw.gst_amount);
  const grand = num(inw.grand_total);
  const goods = grand - gst - charges;

  // ── Section 1: bill lines ──
  const itemRows = items.map((it) => ({
    jumbo: it.jumbo_name,
    spec: `${FORMATTERS.int(it.default_meters)} m · ${it.width_inches}"`,
    quantity: it.quantity,
    rate: it.rate,
    amount: it.amount,
    gst_amount: it.gst_amount,
    line_total: num(it.amount) + num(it.gst_amount),
  }));

  const itemQty = items.reduce((s, it) => s + num(it.quantity), 0);
  const itemAmount = items.reduce((s, it) => s + num(it.amount), 0);
  const itemGst = items.reduce((s, it) => s + num(it.gst_amount), 0);

  // ── Section 2: rolls ──
  // A roll short of its spec changes what it can be expected to
  // yield, so it is flagged here rather than discovered later.
  const rollRows = rolls.map((r) => {
    const short = num(r.total_meters) < num(r.spec_meters);
    return {
      roll: r.vendor_roll_number,
      jumbo: short ? `${r.jumbo_name}  (SHORT)` : r.jumbo_name,
      total_meters: r.total_meters,
      base_cost: r.base_cost,
      gst_alloc: r.gst_alloc,
      charge_alloc: r.charge_alloc,
      landed_cost: r.landed_cost,
      cost_per_meter: r.cost_per_meter,
      __tone: short ? 'expense' : null,
    };
  });

  const totalMeters = rolls.reduce((s, r) => s + num(r.total_meters), 0);
  const totalBase = rolls.reduce((s, r) => s + num(r.base_cost), 0);
  const totalGstAlloc = rolls.reduce((s, r) => s + num(r.gst_alloc), 0);
  const totalChargeAlloc = rolls.reduce((s, r) => s + num(r.charge_alloc), 0);
  const totalLanded = rolls.reduce((s, r) => s + num(r.landed_cost), 0);
  const avgCpm = totalMeters > 0 ? totalLanded / totalMeters : 0;

  const totalsBox = [
    { label: 'Goods Value', value: goods, fmt: 'money' },
    { label: 'GST', value: gst, fmt: 'money' },
  ];
  if (freight) totalsBox.push({ label: 'Freight / Builty', value: freight, fmt: 'money' });
  if (labour) totalsBox.push({ label: 'Labour', value: labour, fmt: 'money' });
  if (other) totalsBox.push({ label: 'Other Charges', value: other, fmt: 'money' });
  totalsBox.push({
    label: 'Landed Total',
    value: grand,
    fmt: 'money',
    emphasis: true,
    tone: 'net',
  });

  return {
    kind: 'statement',
    orientation: 'l',
    title: 'Goods Receipt Note',
    subtitle: `Bill ${inw.bill_number || '—'}`,

    boxes: [
      {
        heading: 'Vendor Details',
        rows: [
          { label: 'Vendor', value: inw.vendor_name },
          { label: 'Address', value: inw.vendor_address },
          { label: 'Contact', value: inw.vendor_contact_person },
          { label: 'Phone', value: inw.vendor_phone },
          {
            label: 'NTN / STRN',
            value: `${inw.vendor_ntn || '—'}  /  ${inw.vendor_strn || '—'}`,
          },
        ],
      },
      {
        heading: 'Receipt Details',
        rows: [
          {
            label: 'Bill Number',
            value: inw.bill_number,
            label2: 'Received',
            value2: FORMATTERS.date(inw.received_date),
          },
          {
            label: 'Against PO',
            value: inw.po_number,
            label2: 'Rolls',
            value2: rolls.length,
          },
          {
            label: 'Total Meters',
            value: FORMATTERS.meters(totalMeters),
            label2: 'Avg Cost / m',
            value2: FORMATTERS.money2(avgCpm),
          },
          {
            label: 'Pay Terms',
            value: inw.vendor_payment_terms_days
              ? `${inw.vendor_payment_terms_days} days`
              : 'On delivery',
          },
          { label: 'Notes', value: inw.notes },
        ],
      },
    ],

    sections: [
      {
        title: 'Bill Details',
        tone: 'net',
        columns: [
          { key: 'jumbo', label: 'Jumbo', align: 'left', fmt: 'text' },
          { key: 'spec', label: 'Specification', align: 'left', fmt: 'text', width: 40 },
          { key: 'quantity', label: 'Rolls', align: 'right', fmt: 'qty', width: 18 },
          { key: 'rate', label: 'Rate / Roll', align: 'right', fmt: 'money', width: 26 },
          { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28 },
          { key: 'gst_amount', label: 'GST', align: 'right', fmt: 'money', width: 24 },
          { key: 'line_total', label: 'Line Total', align: 'right', fmt: 'money', width: 28, bold: true },
        ],
        rows: itemRows,
        footRow: [
          'TOTAL',
          '',
          FORMATTERS.qty(itemQty),
          '',
          FORMATTERS.money(itemAmount),
          FORMATTERS.money(itemGst),
          FORMATTERS.money(itemAmount + itemGst),
        ],
      },
      {
        title: 'Rolls Received',
        tone: 'net',
        columns: [
          { key: 'roll', label: 'Roll #', align: 'left', fmt: 'text', width: 28 },
          { key: 'jumbo', label: 'Jumbo', align: 'left', fmt: 'text' },
          { key: 'total_meters', label: 'Meters', align: 'right', fmt: 'meters', width: 26 },
          { key: 'base_cost', label: 'Base Cost', align: 'right', fmt: 'money', width: 26 },
          { key: 'gst_alloc', label: 'GST', align: 'right', fmt: 'money', width: 22 },
          { key: 'charge_alloc', label: 'Charges', align: 'right', fmt: 'money', width: 24 },
          { key: 'landed_cost', label: 'Landed Cost', align: 'right', fmt: 'money', width: 28, bold: true },
          { key: 'cost_per_meter', label: 'Cost / m', align: 'right', fmt: 'money2', width: 22 },
        ],
        rows: rollRows,
        footRow: [
          `${rolls.length} ROLL${rolls.length === 1 ? '' : 'S'}`,
          '',
          FORMATTERS.meters(totalMeters),
          FORMATTERS.money(totalBase),
          FORMATTERS.money(totalGstAlloc),
          FORMATTERS.money(totalChargeAlloc),
          FORMATTERS.money(totalLanded),
          FORMATTERS.money2(avgCpm),
        ],
      },
    ],

    totals: totalsBox,

    termsHeading: 'NOTES:',
    terms: [
      `Goods and GST of ${FORMATTERS.money(goods + gst)} post to the vendor's account.`,
      'Freight, labour and other charges are paid separately and load onto the rolls by value.',
      'Landed cost is what each roll cost to get onto the floor; cost per meter carries downstream to every core cut from it.',
      'Rolls marked SHORT arrived below their jumbo specification.',
    ],

    signatures: ['Received By', 'Store Keeper', 'Approved By'],
  };
};

export default inwardPrintConfig;